import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { HooksDisplayGrid } from 'ui-components/components/hooks-display-grid';

import { ExternalInventoryEmptyState } from './components/empty-state/external-inventory-empty-state';
import ErrorBoundary from '../../../../components/error-boundary';
import GridLoading from '../../../../components/grid-loading';
import { UserSettings } from '../../../../enums/user-settings/user-settings';
import { useUserSetting } from '../../../../hooks/use-user-setting';
import { saveUserSettingGridConfig } from '../../../../store/user/user.actions';
import style from '../../../grid-page.module.less';
import EmptyGridPlaceholder from '../grid-placeholder';
import { aggregatedExternalInventoryColumns, externalInventoryColumns } from './external-inventory-columns';
import { ExternalInventoryContext } from './external-inventory-context';
import { externalInventoryDefaultConfigs } from './external-inventory-default-column-configuration';
import { useExternalInventoryConnectionsContext } from '../../external-inventory-connections-context';

const statusViewId = 'externalinventory';
const activeFilterViewId = 'default';

const getColumnId = ({ id }) => id;

/**
 * Builds grid columns by merging column configuration with column definitions
 * @param {Array<object>} columnConfig - User's column configuration
 * @param {object} columnsMap - Map of column definitions
 * @returns {Array<object>} Built columns with render functions
 */
const buildColumns = (columnConfig, columnsMap) =>
  columnConfig
    .map((col) => {
      const colToRender = columnsMap[col.mappedId] || columnsMap[col.id];
      if (colToRender) {
        return {
          ...col,
          name: colToRender.name,
          render: colToRender.render,
        };
      }
      return null;
    })
    .filter((col) => !!col);

/**
 * External Inventory Grid Component
 * Displays external inventory data in a grid with column customization
 * @returns {React.ReactElement} The grid component
 */
export const ExternalInventoryGrid = () => {
  const { entitiesLoading, setSelectedProducts, selectedProducts, externalInventoryData, inventoryProductIds } =
    useContext(ExternalInventoryContext);
  const { hasEnabledConnections } = useExternalInventoryConnectionsContext();
  const { externalInventorySourceId } = useParams();
  const [gridColumns, setGridColumns] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);

  const columnsMap = externalInventorySourceId ? externalInventoryColumns : aggregatedExternalInventoryColumns;

  const gridData = useMemo(() => {
    const result =
      inventoryProductIds?.reduce((acc, id) => {
        const foundRows = externalInventoryData.filter((item) => item.productId.toString() === id);

        if (foundRows.length > 0) {
          acc.push(foundRows.length > 1 ? foundRows : foundRows[0]);
        }
        return acc;
      }, []) ?? [];

    externalInventoryData
      .filter((item) => !item.productId || item.productId === '0')
      .forEach((item) => {
        const newItem = {
          ...item,
          sku: null,
        };
        result.push(newItem);
      });

    return result;
  }, [externalInventoryData, inventoryProductIds]);

  const isLoading = entitiesLoading;
  const dispatch = useDispatch();

  const userSetColumnConfiguration = useUserSetting(UserSettings.externalInventoryGridDefaultConfig.settingName);

  const columnConfiguration = useMemo(() => {
    const parsedUserColumnConfiguration = JSON.parse(userSetColumnConfiguration || '[]');

    if (parsedUserColumnConfiguration.length > 0) {
      // Merge saved config with new defaults, keeping new columns
      const mergedConfig = [...parsedUserColumnConfiguration];

      // Add any new columns from externalInventoryDefaultConfigs that don't exist in saved config
      externalInventoryDefaultConfigs.forEach((defaultColumn) => {
        const existsInSaved = parsedUserColumnConfiguration.some((saved) => saved.id === defaultColumn.id);
        if (!existsInSaved) {
          mergedConfig.push(defaultColumn);
        }
      });

      // Sort by index and map to full config
      return mergedConfig
        .sort((a, b) => (a.index || 0) - (b.index || 0))
        .map((savedConfig) => {
          const fullConfig = externalInventoryDefaultConfigs.find((c) => c.id === savedConfig.id);
          return { ...fullConfig, ...savedConfig };
        });
    }
    return externalInventoryDefaultConfigs;
  }, [userSetColumnConfiguration]);

  useEffect(() => {
    setGridColumns(buildColumns(columnConfiguration, columnsMap));
  }, [columnConfiguration, columnsMap]);

  const onColumnsChanged = useCallback(
    (columns) => {
      const action = saveUserSettingGridConfig('externalInventory', statusViewId, activeFilterViewId, columns);
      dispatch(action);
      setGridColumns(buildColumns(columns, columnsMap));
    },
    [dispatch, columnsMap],
  );

  return (
    <div className={style.grid}>
      {isLoading ? (
        <GridLoading />
      ) : (
        <>
          {gridData.length === 0 && !hasEnabledConnections && <ExternalInventoryEmptyState />}
          {gridData.length === 0 && hasEnabledConnections ? <EmptyGridPlaceholder /> : null}
          {gridData.length > 0 && (
            <ErrorBoundary errorMessageId='inventoryGrid'>
              <HooksDisplayGrid
                columns={gridColumns}
                data={gridData}
                expandedRows={expandedRows}
                getColumnId={getColumnId}
                onColumnsChanged={onColumnsChanged}
                onExpandedRowsChanged={setExpandedRows}
                onSelectedChanged={setSelectedProducts}
                selected={selectedProducts}
              />
            </ErrorBoundary>
          )}
        </>
      )}
    </div>
  );
};
